import React, { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Entity } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson } from '@debbie/common/dist/customers/index.js';
import { getLocale } from '@debbie/common/dist/phone/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { cancelDebounce, debounce } from '@debbie/common/dist/util/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { selectSMS } from '@debbie/cortex/dist/newInteractions/selectors.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import { Button, Checkbox, Modal, Badge, Intent } from '..//index.js';
import THEME from '../../theme.js';
import { parseText, PARSE_RESULT_TYPE } from '../../utils/templates.js';
import Info from '../Icons/Info.js';
import MentionExplanation from './MentionExplanation.js';
import TemplateSearch from './TemplateSearch.js';
import { toHtml } from './utils.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { filterPropertyTypes } from '@debbie/common/dist/properties/index.js';
const SMSInteraction = (props) => {
    const singleCase = props.case;
    const { creditorId } = props.case;
    const sms = useSelector((s) => selectSMS(s, singleCase.caseId));
    const lang = useSelector((s) => s.lang);
    const tenant = useSelector(selectTenant);
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [mentionExplanationModalOpen, setMentionExplanationModalOpen] = useState(false);
    const creditorLoadable = useCreditor(creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const [visibleToClients, setVisibleToClients] = useState(true);
    const [reset, setReset] = useState(false);
    const [content, setContent] = useState(sms.editorState);
    useEffect(() => {
        if (reset) {
            setContent(sms.editorState);
            setReset(false);
        }
    }, [sms.editorState]);
    const toggleMentionExplanation = () => {
        setMentionExplanationModalOpen(!mentionExplanationModalOpen);
    };
    const contactPerson = getContactPerson(props.customer);
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.SMS, propertyTypes
        ? filterPropertyTypes(propertyTypes, Entity.Case, 'case_groups', props.case?.caseGroup?.id || null)
        : undefined, tenant);
    const sendSMS = async () => {
        if (!collector)
            return alert('Error');
        try {
            cancelDebounce('sms-content');
            setReset(true);
            c.newInteractions.sendSMS(singleCase.caseId, Entity.Case, sms, {
                from: collector.nickname,
                countryCode: (contactPerson &&
                    contactPerson.details.phone &&
                    contactPerson.details.phone.locale) ||
                    (tenant != null
                        ? getLocale(tenant.countryCode, contactPerson?.details.phone?.number)
                        : '45'),
                number: contactPerson &&
                    contactPerson.details.phone &&
                    contactPerson.details.phone.number,
                text: parseText(toHtml(content), mentionSuggestions, PARSE_RESULT_TYPE.TEXT),
            }, visibleToClients ? 'client' : 'caseworker', sms.intent);
        }
        catch (error) {
            alert('Error');
        }
    };
    const phoneValidation = contactPerson &&
        contactPerson.details.phoneValidations &&
        contactPerson.details.phoneValidations.find((pV) => (pV.phone && pV.phone.number) ==
            (contactPerson.details.phone && contactPerson.details.phone.number) &&
            (pV.phone && pV.phone.locale) ==
                (contactPerson.details.phone && contactPerson.details.phone.locale));
    return (React.createElement(React.Fragment, null,
        React.createElement(TemplateSearch, { interaction: sms, templateType: TemplateType.SMS, case: singleCase, customer: props.customer, onChange: (state) => {
                setContent(state);
            } }),
        React.createElement("div", null,
            []
                .concat(phoneValidation &&
                phoneValidation.originalCarrier &&
                phoneValidation.originalCarrier.name
                ? {
                    content: phoneValidation.originalCarrier.name,
                }
                : [])
                .concat(phoneValidation &&
                phoneValidation.originalCarrier &&
                phoneValidation.originalCarrier.networkType
                ? {
                    content: 
                    // @ts-ignore
                    lang.phoneValidation[phoneValidation.originalCarrier.networkType] || phoneValidation.originalCarrier.networkType,
                }
                : [])
                .concat(phoneValidation &&
                phoneValidation.roaming &&
                phoneValidation.roaming.status &&
                phoneValidation.roaming.status != 'not_roaming'
                ? {
                    content: 
                    // @ts-ignore
                    lang.phoneValidation[phoneValidation.roaming.status] ||
                        phoneValidation.roaming.status,
                }
                : [])
                .concat(phoneValidation &&
                phoneValidation.reachable &&
                !['reachable', 'unknown'].includes(phoneValidation.reachable)
                ? {
                    content: phoneValidation.reachable,
                }
                : [])
                .map((tag) => (React.createElement(Badge, { theme: THEME.COLOR_ACCENT, style: { marginRight: '5px' } }, tag.content))),
            React.createElement("p", null,
                contactPerson &&
                    contactPerson.details.phone &&
                    contactPerson.details.phone.locale &&
                    `+${contactPerson.details.phone.locale} `,
                contactPerson &&
                    contactPerson.details.phone &&
                    contactPerson.details.phone.number)),
        React.createElement(StyledEditor, null,
            React.createElement(Editor, { toolbarClassName: "hide-toolbar", editorState: content, onEditorStateChange: (newEditorState) => {
                    setContent(newEditorState);
                    debounce('sms-content', () => {
                        c.newInteractions.changeEditorState(singleCase.caseId, 'sms', newEditorState);
                    }, 1000);
                }, stripPastedStyles: true, mention: {
                    separator: ' ',
                    trigger: '@',
                    suggestions: mentionSuggestions.map((suggestion) => ({
                        value: suggestion.label,
                        text: suggestion.label,
                        url: suggestion.label,
                    })),
                } })),
        props.isDebtor && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
            React.createElement(Intent, { intent: sms.intent, editable: true, onEdit: (intent) => {
                    c.newInteractions.changeField(singleCase.caseId, 'sms', 'intent', intent);
                } }))),
        React.createElement(Checkbox, { checked: visibleToClients, onChange: (e) => setVisibleToClients(e.target.checked), placeholder: lang.interactions.visibilityCheckText }),
        React.createElement("br", null),
        React.createElement(Button, { size: "small", loading: sms.loading, disabled: !content, onClick: sendSMS }, lang.interactions.sendSMS),
        React.createElement(Button, { size: "small", theme: "secondary", style: { float: 'right', marginRight: '5px' }, onClick: toggleMentionExplanation },
            React.createElement(Info, null)),
        mentionExplanationModalOpen && (React.createElement(Modal, { onToggle: toggleMentionExplanation, header: lang.interactions.explanation },
            React.createElement(MentionExplanation, { suggestions: mentionSuggestions })))));
};
export default SMSInteraction;
const StyledEditor = styled.div `
  .rdw-editor-wrapper .rdw-editor-main {
    border-top: 1px solid #ccc !important;
    border-top-left-radius: 3px !important;
    border-top-right-radius: 3px !important;
  }
`;
//# sourceMappingURL=SMSInteraction.js.map